import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { MuiModal } from "../../../../../../../../../../components";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import { useGetProjectActivityById } from "../../../../../../../../../../api/services/project/projectApis";
import { InformationBadge } from "../../../../../../../../../../common/informationBadge/informationBadge";
import { ViewManageLocale } from "../../../../../../../../../../components/manageLocale/ViewManageLocale";
import {
  getValueOrBlank,
  replaceHashWithValue,
} from "../../../../../../../../../../helpers/utils";
import { useToast } from "../../../../../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../../../../../redux/slices/appState/appStateSlice";
import { ProjectActivitiesRowValue } from "../../modelsAndSchemas/models";
import { ProjectViewActivityWrapper } from "./ProjectViewActivity.style";
import { ProjectViewActivityDefault } from "./viewActivityDefault/ProjectViewActivityDefault";
import { ProjectViewActivityGeneral } from "./viewActivityGeneral/ProjectViewActivityGeneral";
import { addActivitySiteCoreKeys } from "../../siteCore/SiteCoreKeys";

interface ProjectCreateActivityProps {
  show: boolean;
  handleCloseModal: () => void;
  selectedProjectActivity: ProjectActivitiesRowValue;
  isView?: boolean;
  onClickEditButton?: () => void;
}

export const ProjectViewActivity: FC<ProjectCreateActivityProps> = ({
  show,
  handleCloseModal: onClickCloseModal,
  selectedProjectActivity,
  isView,
  onClickEditButton,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();

  const { commonLabels, fetchLabel } = useSiteCore();
  const [showManageLocalePopup, setShowManageLocalePopup] = useState(false);

  const onClickShowManageLocale = () => {
    setShowManageLocalePopup(true);
  };

  const onClickCloseShowManageLocale = () => {
    setShowManageLocalePopup(false);
  };
  const {
    data: projectActivityData,
    isFetching: isProjectActivityDataFetching,
    isError: isProjectActivityDataError,
  } = useGetProjectActivityById(selectedProjectActivity.id);

  const isProjectFrameworkActivity = useMemo(() => {
    return Boolean(projectActivityData?.projectFramework);
  }, [projectActivityData]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isProjectActivityDataFetching,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isProjectActivityDataFetching]);

  useEffect(() => {
    if (isProjectActivityDataError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isProjectActivityDataError]);

  const title = useMemo(
    () =>
      replaceHashWithValue(
        fetchLabel(
          addActivitySiteCoreKeys.addActivity,
          addActivitySiteCoreKeys.viewActivityPopupTitle,
        ),
        getValueOrBlank(projectActivityData?.activityType?.name),
      ),
    [projectActivityData?.activityType?.name],
  );

  const bannerInfoMessage = useMemo(() => {
    return fetchLabel(
      addActivitySiteCoreKeys.addActivity,
      isProjectFrameworkActivity
        ? addActivitySiteCoreKeys.ThisIsAFrameworkLevelActivity
        : addActivitySiteCoreKeys.ThisIsAProjectSpecificActivity,
    );
  }, [isProjectFrameworkActivity]);

  return (
    <>
      <MuiModal
        open={show}
        onClose={onClickCloseModal}
        title={title}
        primaryButtonTitle=""
        secondaryButtonTitle={commonLabels.close}
        onSecondaryButtonClick={onClickCloseModal}
        tertiaryButtonTitle={
          !isView
            ? fetchLabel(
                addActivitySiteCoreKeys.addActivity,
                addActivitySiteCoreKeys.editActivityButton,
              )
            : ""
        }
        onTertiaryButtonClick={onClickEditButton}
        hasLargeContent
        className="project-view-activity-modal"
      >
        <ProjectViewActivityWrapper>
          <InformationBadge message={bannerInfoMessage} />
          <ProjectViewActivityGeneral
            onClickViewManageLocal={onClickShowManageLocale}
            projectActivityData={projectActivityData!}
          >
            <ProjectViewActivityDefault activityData={projectActivityData!} />
          </ProjectViewActivityGeneral>
        </ProjectViewActivityWrapper>
      </MuiModal>
      {showManageLocalePopup && (
        <ViewManageLocale
          show={showManageLocalePopup}
          handleCloseModal={onClickCloseShowManageLocale}
          localeLanguages={projectActivityData?.localizedTopics ?? []}
          title={title}
          bannerInfoMessage={bannerInfoMessage}
        />
      )}
    </>
  );
};
